import { useLocation, useNavigate } from "react-router-dom";
import { Bell, ChevronLeft } from "lucide-react";
import { useNotifications } from "@/context/NotificationContext";

const Header = ({ showBack = false, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const notif = useNotifications();
  const unread = notif?.unread || 0;

  const goBack = () => {
    // React Router marks the very first in-app entry with key === 'default'.
    // If we're on that entry, there is no in-app history to pop → fall back
    // to Home rather than escaping the site (protects direct-URL landings
    // on mobile Chrome from unexpected about:blank exits).
    if (location.key === "default") {
      navigate("/", { replace: true });
    } else {
      navigate(-1);
    }
  };

  return (
    <header
      className="sticky top-0 z-30 border-b border-gray-100 bg-white/95 backdrop-blur-xl pt-safe"
      data-testid="app-header"
      role="banner"
    >
      <div className="mx-auto flex max-w-screen-md items-center gap-3 px-4 py-3 sm:px-6">
        {showBack ? (
          <>
            <button
              type="button"
              data-testid="header-back-btn"
              onClick={goBack}
              className="grid h-11 w-11 shrink-0 place-items-center text-black hover:text-[#D4AF37]"
              aria-label="Go back"
            >
              <ChevronLeft aria-hidden="true" className="h-6 w-6" />
            </button>
            {title && (
              <h1 className="flex-1 font-heading text-lg font-medium tracking-tight">
                {title}
              </h1>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="header-brand"
            className="flex flex-1 items-baseline gap-2 leading-none"
            aria-label="Royal Lady — go to home"
          >
            <span className="font-heading text-2xl font-semibold tracking-tight text-black sm:text-3xl">
              Royal
            </span>
            <span className="font-heading text-2xl font-semibold tracking-tight text-gold-gradient sm:text-3xl">
              Lady
            </span>
            <span aria-hidden="true" className="ml-1 hidden text-[9px] uppercase tracking-[0.35em] text-gray-400 sm:inline">
              Est. 2026
            </span>
          </button>
        )}

        <button
          type="button"
          data-testid="header-notifications-btn"
          onClick={() => navigate("/notifications")}
          className="relative grid h-11 w-11 shrink-0 place-items-center text-black hover:text-[#D4AF37]"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        >
          <Bell aria-hidden="true" className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="header-notifications-badge"
              aria-hidden="true"
              className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#B8912B] px-1 text-[9px] font-bold text-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </div>
    </header>
  );
};

export default Header;
